import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getEmailTemplate, getEmailTemplateList, getLandingPage, getlandingpages, getwebsitecontent, deleteSimulationTemplate } from '../../../API/PhishingAPI';
import HtmlEditor from '../HtmlEditor';
import { ImportSiteModal } from '../LandingPage/ImportSiteModal';
import { ImportEmailTemplateModal } from '../EmailTemplate/ImportEmailTemplateModal';
import { copyTemplateSet, createBlankTemplate, createSocialTemplate, pairLibraryTemplates, saveTemplateSet, socialTemplates } from './templateSets';
import { groupTemplateSets, matchesTemplateSearch, templateOrganization } from './templateOrganization';
import './TemplateGallery.css';

const libraryHeaders = { domain: '0.template' };

export function TemplateGallery({ kind = 'page', items, loading, error, onRetry, onImported, onDeleted, sets, draft,
  editorOnly = false, onClose, primaryActionLabel, contextHelp, t }) {
  const [counterparts, setCounterparts] = useState(null);
  const [libraryError, setLibraryError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selection, setSelection] = useState(null);
  const [view, setView] = useState(kind);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [emailFormat, setEmailFormat] = useState('html');
  const [importing, setImporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);
  const importPending = useRef(false);
  const saved = useRef({});
  const openingRequest = useRef(false);
  const saving = useRef(false);
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (sets || editorOnly) return undefined;
    let active = true;
    setLibraryError(false);
    const load = kind === 'page' ? getEmailTemplateList : getlandingpages;
    load(libraryHeaders).then(data => { if (active) setCounterparts(data); })
      .catch(() => { if (active) setLibraryError(true); });
    return () => { active = false; };
  }, [kind, retry, sets, editorOnly]);

  useEffect(() => {
    if (!draft) return;
    saved.current = {};
    setSaveError('');
    setSelection(copyTemplateSet(draft));
    setView('message');
    setEmailFormat('html');
    setEditing(true);
  }, [draft]);

  useEffect(() => {
    if (sets && categoryFilter && !groupTemplateSets(sets, t).some(group => (group.key || 'uncategorized') === categoryFilter)) {
      setCategoryFilter('');
    }
  }, [sets, categoryFilter, t]);

  async function openTemplate(set, nextView) {
    if (openingRequest.current) return;
    openingRequest.current = true;
    setOpening(set.id);
    setSaveError('');
    try {
      let full = set;
      if (set.library) {
        const [page, message] = await Promise.all([
          set.page ? getLandingPage(set.page.id, libraryHeaders) : null,
          set.message ? getEmailTemplate(set.message.id, libraryHeaders) : null
        ]);
        const blank = createBlankTemplate(t);
        const organization = templateOrganization(set, t);
        full = { ...set, category: organization.categoryKey ? organization.category : '',
          page: page || blank.page, message: message || blank.message, fromSingle: !page || !message };
      }
      if (!mounted.current) return;
      saved.current = {};
      setSelection(copyTemplateSet({ ...full,
        source: { ...full.source, originalName: full.source?.originalName || full.name } }));
      setView(nextView);
      setEmailFormat('html');
      setEditing(!!full.existing);
    } catch (failure) {
      if (mounted.current) toast.error(t('template_gallery.load_error'));
    } finally {
      openingRequest.current = false;
      if (mounted.current) setOpening(null);
    }
  }

  function close() {
    if (!saving.current && !importPending.current) { setSelection(null); onClose?.(); }
  }

  function updateContent(field, value) {
    setSelection(current => ({ ...current, [view]: { ...current[view], [field]: value } }));
  }

  function importBusy(value) { importPending.current = value; setImporting(value); }

  async function importSite(url) {
    const result = await getwebsitecontent(url);
    if (typeof result.html !== 'string') throw new Error(t('template_import.site_error'));
    setSelection(current => ({ ...current, page: { ...current.page, html: result.html } }));
    setEditing(true);
  }

  function importEmail(result) {
    setSelection(current => ({ ...current, message: { ...current.message,
      html: result.html || '', text: result.text || '', subject: result.subject || '' } }));
    setEmailFormat(result.html ? 'html' : 'text');
    setEditing(true);
  }

  function updateSet(field, value) {
    setSelection(current => ({ ...current, [field]: value,
      ...(field === 'category' ? { categorySuggested: false } : {}) }));
  }

  function closeDelete() {
    if (!deletePending.current) { setDeleteTarget(null); setDeleteError(''); }
  }

  async function confirmDelete() {
    if (deletePending.current || !deleteTarget?.existing || deleteTarget.library || !sets) return;
    deletePending.current = true; setDeleting(true); setDeleteError('');
    try {
      const result = await deleteSimulationTemplate(deleteTarget.id);
      if (!mounted.current) return;
      const id = deleteTarget.id;
      setDeleteTarget(null);
      toast.success(t('template_gallery.delete_success'));
      onDeleted?.(id, result);
    } catch (error) {
      if (mounted.current) setDeleteError(error.code === 409 ? t('template_gallery.delete_in_use')
        : t('template_gallery.delete_error', { message: error.message || '' }));
    } finally {
      deletePending.current = false;
      if (mounted.current) setDeleting(false);
    }
  }

  async function importSet() {
    if (saving.current || importPending.current || !selection.name.trim() || !selection.message?.subject?.trim()) return;
    saving.current = true;
    setBusy(true);
    setSaveError('');
    try {
      const result = await saveTemplateSet(selection, saved.current, () => {});
      if (!mounted.current) return;
      toast.success(t(selection.page && selection.message ? 'template_gallery.success' : 'template_gallery.single_success'));
      setSelection(null);
      onImported({ ...selection, id: result.message.id, existing: true, library: false,
        page: { ...selection.page, ...result.page }, message: { ...selection.message, ...result.message } });
    } catch (failure) {
      if (mounted.current) setSaveError(t(Object.keys(saved.current).length ? 'template_gallery.partial_error' : 'template_gallery.save_error', {
        message: failure.message || ''
      }));
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  function cards(cardSets) {
    return <div className="template-gallery__grid">
      {cardSets.map(set => <article key={set.id} className="template-gallery__card">
        <div className="template-gallery__card-heading">
          <span className="template-gallery__icon" aria-hidden="true"><i className={`pe-7s-${set.icon}`} /></span>
          <h4>{templateOrganization(set, t).title}</h4>
        </div>
        <p className="template-gallery__description">{set.description || t('template_gallery.no_description')}</p>
        <p className="template-gallery__type"><strong>{t('template_gallery.type')}:</strong>{' '}
          {set.library ? t(set.page && set.message ? 'template_gallery.complete_set' : 'template_gallery.single_template') : t('template_gallery.click_data')}
        </p>
        {set.existing && !set.page && <p className="text-danger">{t('template_gallery.missing_page')}</p>}
        <div className="template-gallery__actions">
          <button type="button" className="template-gallery__landing" disabled={!set.page || !!opening}
            onClick={() => openTemplate(set, 'page')} aria-label={`${t('template_gallery.landing')}: ${templateOrganization(set, t).title}`}>
            {opening === set.id ? <Spinner size="sm" /> : t('template_gallery.landing')}
          </button>
          <button type="button" disabled={!set.message || !!opening}
            onClick={() => openTemplate(set, 'message')} aria-label={`${t('template_gallery.message')}: ${templateOrganization(set, t).title}`}>
            {t('template_gallery.message')}
          </button>
        </div>
        {sets && set.existing && !set.library && <button type="button" className="template-gallery__delete"
          disabled={!!opening || busy || importing || deleting}
          aria-label={`${t('template_gallery.delete')}: ${templateOrganization(set, t).title}`}
          onClick={() => { setDeleteError(''); setDeleteTarget(set); }}>
          <i className="pe-7s-trash" aria-hidden="true" /> {t('template_gallery.delete')}
        </button>}
      </article>)}
    </div>;
  }

  const library = kind === 'page' ? pairLibraryTemplates(items || [], counterparts || [])
    : pairLibraryTemplates(counterparts || [], items || []);
  const partial = Object.keys(saved.current).length > 0;
  const content = selection?.[view];
  const working = busy || importing;
  const allSets = sets || [...socialTemplates.map(model => createSocialTemplate(model, t)), ...library];
  const groups = groupTemplateSets(allSets, t);
  const filtered = allSets.filter(set => {
    const organization = templateOrganization(set, t);
    return (!categoryFilter || (organization.categoryKey || 'uncategorized') === categoryFilter)
      && matchesTemplateSearch(set, query, t);
  });
  const organized = groupTemplateSets(filtered, t);

  return <section className={editorOnly ? undefined : 'template-gallery'} style={editorOnly ? { padding: 0 } : undefined} aria-label={t('template_gallery.title')}>
    {!editorOnly && <>
    {!sets && <>
    <h2>{t('template_gallery.title')}</h2>
    <div className="template-gallery__intro">
      <p>{t('template_gallery.intro_set')}</p>
      <p>{t('template_gallery.intro_edit')}</p>
    </div>
    </>}
    {!!allSets.length && <div className="template-gallery__filters">
      <div><Label for="template-search">{t('template_gallery.search')}</Label>
        <Input id="template-search" type="search" value={query} placeholder={t('template_gallery.search_placeholder')}
          onChange={event => setQuery(event.target.value)} /></div>
      <div><Label for="template-category-filter">{t('template_gallery.category')}</Label>
        <Input id="template-category-filter" type="select" value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)}>
          <option value="">{t('template_gallery.all_categories')}</option>
          {groups.map(group => <option key={group.key} value={group.key || 'uncategorized'}>{group.label}</option>)}
        </Input></div>
    </div>}
    {organized.map(group => <section key={group.key} className="template-gallery__category-section" aria-label={group.label}>
      <div className="template-gallery__section-heading"><h3>{group.label}</h3><span>{group.count}</span></div>
      {cards(group.sets)}
    </section>)}
    {!filtered.length && <p className="template-gallery__empty">{t(allSets.length ? 'template_gallery.no_results' : 'template_gallery.no_sets')}</p>}
    {!sets && ((error || libraryError) ? <Alert color="warning">{t('template_gallery.library_error')}
      <Button color="link" onClick={() => { onRetry(); setRetry(value => value + 1); }}>{t('template_gallery.retry')}</Button>
    </Alert> : loading || !items || !counterparts ? <div role="status"><Spinner size="sm" /> {t('template_gallery.loading')}</div>
      : !library.length && <p className="template-gallery__empty">{t('template_gallery.empty')}</p>)}

    </>}
    {selection && <Modal isOpen toggle={close} size="xl" scrollable className="template-gallery-modal" backdrop={working || partial ? 'static' : true}>
      <ModalHeader toggle={working ? undefined : close}>{templateOrganization(selection, t).title}</ModalHeader>
      <ModalBody>
        <div className="template-gallery__preview-toolbar">
          <div role="group" aria-label={t('template_gallery.preview')}>
            {['page', 'message'].filter(type => selection[type]).map(type => <button type="button" key={type}
              className={view === type ? 'is-active' : ''} aria-pressed={view === type} disabled={working}
              onClick={() => setView(type)}>{t(type === 'page' ? 'template_gallery.landing' : 'template_gallery.message')}</button>)}
          </div>
          <Button color="link" disabled={working || partial} onClick={() => setEditing(value => !value)}>
            {t(editing ? 'template_gallery.preview' : 'template_gallery.customize')}
          </Button>
        </div>
        <p className="template-gallery__modal-help">{contextHelp || t(selection.library ? 'template_gallery.local_copy' : 'template_gallery.editor_help')}</p>
        {selection.fromSingle && <Alert color="warning" className="py-2">{t('template_gallery.complete_external')}</Alert>}
        {saveError && <Alert color="danger" role="alert">{saveError}</Alert>}
        <Label for="template-set-name">{t('template_gallery.name')}</Label>
        <Input id="template-set-name" value={selection.name} disabled={busy || partial} required
          onChange={event => updateSet('name', event.target.value)} />
        <details className="template-gallery__metadata">
          <summary>{t('template_gallery.details')}</summary>
          <div className="template-gallery__metadata-fields">
            <div><Label for="template-set-description" className="mt-3">{t('template_gallery.description')}</Label>
              <Input id="template-set-description" type="textarea" rows="2" maxLength={1000} value={selection.description || ''}
                placeholder={t('template_gallery.no_description')} disabled={busy || partial}
                onChange={event => updateSet('description', event.target.value)} /></div>
            <div><Label for="template-set-category" className="mt-3">{t('template_gallery.category')}</Label>
              <Input id="template-set-category" value={selection.category || ''} maxLength={80}
                placeholder={t('template_gallery.no_category')} disabled={busy || partial}
                onChange={event => updateSet('category', event.target.value)} />
              {selection.categorySuggested && <small className="text-muted">{t('template_gallery.category_suggestion')}</small>}</div>
          </div>
          {selection.library && <p className="template-gallery__source">{t('template_gallery.original_name')}: {selection.source.originalName}</p>}
        </details>
        {editing && !partial && (view === 'page'
          ? <ImportSiteModal t={t} importSite={importSite} onBusyChange={importBusy} disabled={working} />
          : <ImportEmailTemplateModal t={t} onImported={importEmail} onBusyChange={importBusy} disabled={working} />)}
        {view === 'message' && <div className="template-gallery__subject">
          <Label for="template-set-subject">{t('template_gallery.subject_label')}</Label>
          <Input id="template-set-subject" value={content.subject || ''} disabled={!editing || working || partial}
            onChange={event => updateContent('subject', event.target.value)} />
        </div>}
        {view === 'message' && <div className="btn-group mb-3" role="group" aria-label={t('template_import.email_format')}>
          {['text', 'html'].map(format => <Button key={format} color={emailFormat === format ? 'primary' : 'light'}
            aria-pressed={emailFormat === format} disabled={working} onClick={() => setEmailFormat(format)}>
            {format === 'html' ? 'HTML' : t('template_import.plaintext')}
          </Button>)}
        </div>}
        {view === 'message' && emailFormat === 'text' ? (editing && !busy && !partial
          ? <Input type="textarea" rows="14" aria-label={t('template_import.plaintext')} value={content.text || ''}
            disabled={working} onChange={event => updateContent('text', event.target.value)} />
          : <pre className="template-gallery__plaintext">{content.text}</pre>)
          : editing && !busy && !partial ? <HtmlEditor key={view} value={content.html || ''} name={`template-set-${view}`}
          onChange={value => updateContent('html', value)} />
          : <iframe className="template-gallery__preview" title={`${t('template_gallery.preview')}: ${selection.name} — ${t(view === 'page' ? 'template_gallery.landing' : 'template_gallery.message')}`}
            sandbox="" srcDoc={(content.html || '').replace(/\{\{\.URL\}\}/g, '#')} />}
        {editing && view === 'page' && <div className="mt-3">
          <Label check className="ml-4">
            <Input type="checkbox" checked={!!content.capture_credentials} disabled={busy || partial}
              onChange={event => updateContent('capture_credentials', event.target.checked)} />
            {t('template_gallery.capture_data')}
          </Label>
          <Label for="template-set-redirect" className="d-block mt-3">{t('template_gallery.redirect')}</Label>
          <Input id="template-set-redirect" value={content.redirect_url || ''} disabled={busy || partial}
            onChange={event => updateContent('redirect_url', event.target.value)} />
        </div>}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={close} disabled={working}>{t('template_gallery.cancel')}</Button>
        <Button color="primary" onClick={importSet} disabled={working || !selection.page || !selection.message || !selection.name.trim() || !selection.message.subject?.trim()}>
          {busy && <Spinner size="sm" className="mr-2" />}
          {!busy && !partial && primaryActionLabel ? primaryActionLabel :
            t(busy ? 'template_gallery.saving' : partial ? 'template_gallery.retry' : selection.existing ? 'template_gallery.save_changes' : 'template_gallery.use_set')}
        </Button>
      </ModalFooter>
    </Modal>}
    {deleteTarget && <Modal isOpen toggle={closeDelete} backdrop={deleting ? 'static' : true}>
      <ModalHeader toggle={deleting ? undefined : closeDelete}>{t('template_gallery.delete_title')}</ModalHeader>
      <ModalBody>
        <p>{t('template_gallery.delete_confirm', { name: templateOrganization(deleteTarget, t).title })}</p>
        <p className="text-muted mb-0">{t('template_gallery.delete_details')}</p>
        {deleteError && <Alert color="danger" role="alert" className="mt-3 mb-0">{deleteError}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={closeDelete} disabled={deleting}>{t('template_gallery.delete_cancel')}</Button>
        <Button color="danger" onClick={confirmDelete} disabled={deleting}>
          {deleting && <Spinner tag="span" size="sm" className="mr-2" />}
          {t(deleting ? 'template_gallery.deleting' : 'template_gallery.delete_confirm_action')}
        </Button>
      </ModalFooter>
    </Modal>}
  </section>;
}

export default withTranslation()(TemplateGallery);
